import * as ToggleGroup from '@radix-ui/react-toggle-group';
import classNames from 'classnames';
import { useMemo } from 'react';

import { MWebCardSelectItem } from './components/MWebCardSelectItem';
import type { MWebCardSelectProps } from './types';

import './mweb-card-select.scss';

export const MWebCardSelect = ({
  className,
  options = [],
  optionCardSize = 'large',
  type,
  value,
  allowDeselect = true,
  onOptionSelect,
  withCursorPointer = false,
  priceSize,
  clickButtonToSelect = false,
  disabled = false,
  contactsTierSelectWidget,
  alignContent = 'center',
  showBulletTicks = false,
  oldValuePlacement,
  popularBannerText,
  onRemoveSms,
}: MWebCardSelectProps) => {
  const topContainerClassName = classNames('payflow-ui mweb-card-select', optionCardSize, className, type);
  const innerContainerClassName = classNames(
    'mweb-card-select-option-container',
    withCursorPointer && 'with-pointer',
    alignContent === 'left' && 'align-left',
    type
  );

  const orderedOptions = useMemo(
    () => options.sort((a, b) => (a.mWebVariantContent?.priority ?? 0) - (b.mWebVariantContent?.priority ?? 0)),
    [options]
  );

  if (clickButtonToSelect) {
    return (
      <div className={topContainerClassName} style={{ '--card-options-amount': options.length } as React.CSSProperties}>
        {orderedOptions.map((option) => (
          <section
            key={option.id}
            className={classNames(
              innerContainerClassName,
              option.mWebVariantContent?.showPopularBanner && 'with-popular-banner'
            )}
          >
            {option.mWebVariantContent?.showPopularBanner && popularBannerText && (
              <div className="mweb-card-select-option__popular-banner">{popularBannerText}</div>
            )}
            <MWebCardSelectItem
              option={option}
              optionCardSize={optionCardSize}
              value={value}
              onOptionSelect={onOptionSelect}
              withCursorPointer={withCursorPointer}
              priceSize={priceSize}
              clickButtonToSelect
              disabled={option.disabled || disabled}
              type={type}
              contactsTierSelectWidget={contactsTierSelectWidget}
              showBulletTicks={showBulletTicks}
              oldValuePlacement={oldValuePlacement}
              onRemoveSms={onRemoveSms}
            />
          </section>
        ))}
      </div>
    );
  }

  return (
    <ToggleGroup.Root
      className={topContainerClassName}
      value={value}
      type="single"
      onValueChange={(newValue) => {
        if (!onOptionSelect) {
          return;
        }

        if (!newValue && !allowDeselect) {
          return;
        }

        onOptionSelect(newValue);
      }}
    >
      {options.map((option) => (
        <ToggleGroup.Item
          key={option.id}
          className={innerContainerClassName}
          value={option.id}
          id={option.id}
          disabled={option.disabled}
        >
          {option.highlightedText && (
            <div className="mweb-card-select-option__highlighted-text_container">
              <h6 className="mweb-card-select-option__highlighted-text_container_header">{option.highlightedText}</h6>
            </div>
          )}
          <MWebCardSelectItem
            option={option}
            optionCardSize={optionCardSize}
            value={value}
            onOptionSelect={onOptionSelect}
            withCursorPointer={withCursorPointer}
            priceSize={priceSize}
            type={type}
            contactsTierSelectWidget={contactsTierSelectWidget}
            showBulletTicks={showBulletTicks}
            oldValuePlacement={oldValuePlacement}
            onRemoveSms={onRemoveSms}
          />
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
};
